import {
  FluentProvider,
  teamsLightTheme,
  teamsDarkTheme,
  teamsHighContrastTheme,
  Spinner,
  tokens,
} from "@fluentui/react-components";
import React, { useState, useEffect, useMemo } from "react";
import { BrowserRouter as Router, Navigate, Route, Routes, useNavigate, useLocation } from "react-router-dom";
import { app, teamsCore, menus } from "@microsoft/teams-js";
import { PublicClientApplication, createNestablePublicClientApplication } from '@azure/msal-browser';
import { PolyAuthProvider, usePolyAuth } from '@repo/shared/hooks/PolyAuthContext';
import { SWRProvider } from '@repo/shared/hooks/swrConfig';
import { Box, Button, Typography, ThemeProvider, Tabs, Tab } from "@mui/material";
import { MsalProvider } from '@azure/msal-react';
import {createLightTheme, createDarkTheme} from '@repo/shared/components/theme';
import useDocumentDirection from '@repo/shared/hooks/useDocumentDirection';
import RtlCacheProvider from '@repo/shared/i18n/RtlCacheProvider';

import Meetings from "@repo/shared/components/Meetings";
import Sidebar from "@repo/shared/components/Sidebar";
import StandaloneSidebar from "./StandaloneSidebar";
import CallLogs from "@repo/shared/components/CallLogs";
import { useTranslation } from 'react-i18next';
import { Toaster, toast } from "sonner";
import { scopes } from '@repo/shared/config';
import useLocalStorage from "@repo/shared/hooks/useLocalStorage";
import IntercomSupport from "@repo/shared/components/IntercomSupport";
import IdentityBar from '@repo/shared/components/IdentityBar';

const msalConfig = {
  auth: {
    clientId: process.env.REACT_APP_CLIENT_ID,
    authority: `https://login.microsoftonline.com/organizations`,
    supportsNestedAppAuth: true
  },
  cache: {
    cacheLocation: "localStorage",
    storeAuthStateInCookie: false
  }
};

var msalInstance = null;
export function initializePublicClient() {
  console.log("Starting initializePublicClient for clientId:", msalConfig.auth.clientId);
  return createNestablePublicClientApplication(msalConfig).then(
    (result) => {
      console.log("Client app created");
      msalInstance = result;
      return msalInstance;
    }
  );
}

// Standalone MSAL init (no Teams SDK dependency) for use outside Teams iframe
function initializeStandalonePublicClient() {
  console.log("Initializing standalone MSAL client for clientId:", msalConfig.auth.clientId);
  const standaloneConfig = { ...msalConfig, auth: { ...msalConfig.auth, supportsNestedAppAuth: false } };
  msalInstance = new PublicClientApplication(standaloneConfig);
  return msalInstance.initialize().then(() => msalInstance);
}

const isStandaloneRoute = window.location.pathname === '/ms-teams/sidebar-standalone';


/**
 * The main app which handles the initialization and routing
 * of the app.
 */
export default function App() {
  const [appContext, setAppContext] = useState(null);
  const [accountId, setAccountId] = useState(null);
  const [themeString, setThemeString] = useState("default");
  const direction = useDocumentDirection();
  const muiTheme = useMemo(
    () => themeString === "dark" ? createDarkTheme(direction) : createLightTheme(direction),
    [themeString, direction]
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [verificationJWT, setVerificationJWT] = useLocalStorage('verificationJWT');
  const [frameContext, setFrameContext] = useState(null);

  useEffect(() => {
    // Standalone sidebar route: skip Teams SDK, use standard MSAL
    if (isStandaloneRoute) {
      initializeStandalonePublicClient().then(() => {
        setLoading(null);
      }).catch((err) => {
        console.error('Standalone MSAL init error:', err);
        // Still show the sidebar even if MSAL init fails — we just won't detect sessions
        setLoading(null);
      });
      return;
    }

    if (!app) {
      return;
    }
    console.log("Initializing Teams app...");
    app.initialize(["https://*.actual.video", "https://*.polyguard.ai"]).then(async () => {
      console.log("Teams app initialized.");
      // Notifies that the app initialization is successfully and is ready for user interaction.
      await initializePublicClient();
      // Signal to other subdomains that the user has an active MS session.
      // Used by trust check pages to decide whether to auto-redirect or show
      // a "Join Meeting" button (for anonymous users who won't see the in-Teams sidepanel).
      var parentDomain = window.location.hostname.split('.').slice(-2).join('.');
      document.cookie = "pg_msft_session=1; domain=." + parentDomain + "; path=/; max-age=86400; SameSite=Lax";
      console.log("Getting Teams app context...");

      // Get the user context from Teams and set it in the state
      app.getContext().then(async (context) => {
        console.log("Got App Context");
        setAppContext(context);
        setThemeString(context.app.theme || "default");
        setAccountId(context.account?.tenantId);
        setFrameContext(context.page.frameContext);
        // Enable app caching.
        // App Caching was configured in this sample to reduce the reload time of your app in a meeting.
        // To learn about limitations and available scopes, please check https://learn.microsoft.com/en-us/microsoftteams/platform/apps-in-teams-meetings/app-caching-for-your-tab-app.
        if (context.page.frameContext === "sidePanel") {
          teamsCore.registerOnLoadHandler((context) => {
            // Use context.contentUrl to route to the correct page.
            console.log("Teams app loaded");
            app.notifySuccess();
          });

          teamsCore.registerBeforeUnloadHandler((readyToUnload) => {
            // Dispose resources here if necessary.
            // Notify readiness by invoking readyToUnload.
            readyToUnload();
            return true;
          });
          setLoading(null);
        } else {
          console.log("Not in sidePanel frame context, skipping app caching setup and trying for menus.");
          setLoading(null);
          app.notifySuccess();
        }
      });
    }).catch((error) => {
      console.error('Error getting app context:', error);
      setError(error);

    });
  }, [app]);

  if (!msalInstance) {
    return <Spinner style={{ margin: 100 }} />;
  }

  if (error && !isStandaloneRoute) {
    return (
      // eslint-disable-next-line i18next/no-literal-string
      <Typography variant="h6" color="error" style={{ margin: 20 }}>
        Error: {error.message}
      </Typography>
    );
  }

  return (

    <MsalProvider instance={msalInstance}>
      <RtlCacheProvider>
            <ThemeProvider theme={muiTheme}>
      <FluentProvider
        theme={
          themeString === "dark"
            ? teamsDarkTheme
            : themeString === "contrast"
              ? teamsHighContrastTheme
              : teamsLightTheme
        }
      >
        <IntercomSupport />
        <Box sx={{
          height: '100vh',
          width: '100%',
          maxWidth: '100vw',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          backgroundColor: tokens.colorNeutralBackground2
        }}>
            <Toaster />
            {loading ? (
              <Spinner style={{ margin: 100 }} />
            ) : <InnerApp accountId={accountId} appContext={appContext} frameContext={frameContext} msalInstance={msalInstance} themeString={themeString} />
            }
        </Box>
      </FluentProvider></ThemeProvider></RtlCacheProvider></MsalProvider>
  );
}


// Wrapper to filter accounts to only the tenant-linked account (each Teams tenant maps to one Polyguard account)
function TeamsIdentityBar({ themeString }) {
  const { teamsLinkedAccount } = usePolyAuth();
  const accounts = teamsLinkedAccount ? [teamsLinkedAccount] : [];
  return <IdentityBar themeString={themeString} accounts={accounts} />;
}

const InnerApp = ({ accountId, appContext, frameContext, msalInstance, themeString }) => {
  return (
    <SWRProvider>
    <Router>
      <Routes>
        <Route path="/ms-teams/meetings" element={
          <PolyAuthProvider msalInstance={msalInstance} requireAccount={true}>
            <AuthenticatedComponent><MainView accountId={accountId} appContext={appContext} themeString={themeString} /></AuthenticatedComponent>
          </PolyAuthProvider>
        } />
        <Route path="/ms-teams/sidebar" element={
          frameContext === "sidePanel" ?
            <PolyAuthProvider msalInstance={msalInstance} requireAccount={false} allowAnonymous={true}>
              <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                <Sidebar platform="ms-teams" appContext={appContext} />
              </Box>
            </PolyAuthProvider>
            : <NoSidebarMessage />
        } />
        <Route path="/ms-teams/sidebar-standalone" element={
          <PolyAuthProvider msalInstance={msalInstance} requireAccount={false} allowAnonymous={true}>
            <StandaloneSidebar />
          </PolyAuthProvider>
        } />
        <Route path="/ms-teams/logs/log/:logId" element={
          <PolyAuthProvider msalInstance={msalInstance} requireAccount={true}>
            <AuthenticatedComponent><MainView accountId={accountId} appContext={appContext} themeString={themeString} defaultTab={1} /></AuthenticatedComponent>
          </PolyAuthProvider>
        } />
        <Route path="/ms-teams/logs/log" element={
          <PolyAuthProvider msalInstance={msalInstance} requireAccount={true}>
            <AuthenticatedComponent><MainView accountId={accountId} appContext={appContext} themeString={themeString} defaultTab={1} /></AuthenticatedComponent>
          </PolyAuthProvider>
        } />
        <Route path="/ms-teams/logs" element={<Navigate to="/ms-teams/logs/log" replace />} />
        <Route path="*" element={<Navigate to={"/ms-teams/sidebar"} />}></Route>
      </Routes>
    </Router>
    </SWRProvider>
  );
}

const NoSidebarMessage = () => {
  const { t } = useTranslation('teams');
  return <>{t('error.noSidebarContext')}</>;
};

const MainView = ({ accountId, appContext, themeString, defaultTab = 0 }) => {
  const { t } = useTranslation('teams');
  const navigate = useNavigate();
  const location = useLocation();
  
  const currentTab = location.pathname.startsWith('/ms-teams/logs') ? 1 : 0;
  
  const handleTabChange = (event, newValue) => {
    const tabRoutes = ['/ms-teams/meetings', '/ms-teams/logs/log'];
    navigate(tabRoutes[newValue]);
  };

  return (
    <Box sx={{
      width: '100%',
      maxWidth: '100%',
      boxSizing: 'border-box',
      px: { xs: 1, sm: 2 },
      flex: 1,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
    }}>
      <TeamsIdentityBar themeString={themeString} />
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1, flexShrink: 0 }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          aria-label="management tabs"
          variant="fullWidth"
          sx={{
            '& .MuiTab-root': {
              minWidth: 0,
              px: { xs: 1, sm: 2 },
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
            }
          }}
        >
          <Tab label={t('tabs.meetings')} />
          <Tab label={t('tabs.logs')} />
        </Tabs>
      </Box>
      <Box sx={{ mt: 2, maxWidth: '100%', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {currentTab === 0 ? (
          <Meetings accountId={accountId} appContext={appContext} />
        ) : (
          <CallLogs basePath="/ms-teams/logs" defaultViewMode="user" />
        )}
      </Box>
    </Box>
  );
}

const AuthenticatedComponent = ({ children }) => {
  const { isAuthenticated, msalToken } = usePolyAuth();
  if (!isAuthenticated || !msalToken) {
    return <Spinner style={{ margin: 100 }} />;
  }
  return children;
}